import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tableFromIPC, Table } from 'apache-arrow';
import { Observable, catchError, map, throwError } from 'rxjs';
import { enviroment } from 'src/environments/environment';
import {
  ChannelMetadata,
  ChannelsListResponse,
  LogStatsRequest,
  LogStatsResponse,
} from 'src/app/model/response/log-display';
import { validateDisplayProjections } from 'src/app/shared/log-display.util';

/** Content-Type do sucesso de `channels` (contrato §11.1). */
const ARROW_CONTENT_TYPE = 'application/vnd.apache.arrow.stream';

/** Limites do request de `stats` (contrato §12). */
const MAX_STATS_CHANNELS = 10;
const MIN_STATS_POINTS = 3;
const MAX_STATS_POINTS = 5000;
const MIN_BUCKET_MS = 100;
const MAX_BUCKET_MS = 60000;
const NON_STATS_CHANNELS = ['Sample_Time', 'Sample_Index'];

/** Série lida do Arrow. NaN = ausente (nulo no Arrow); nunca vira zero. */
export type ChannelSeries = Float32Array | Float64Array | Int32Array;

export interface FileChannelsResult {
  /** Eixo X (segundos desde o início do log). */
  sampleTime: Float32Array;
  /**
   * Mapa nome do canal → array de valores (mesmo length que sampleTime).
   * Canais com nulos vêm como Float32Array/Float64Array com NaN nas posições nulas.
   */
  series: Record<string, ChannelSeries>;
  /** Canais pedidos que não existem no arquivo (foram ignorados). */
  missing: string[];
  /** Taxa de amostragem usada pela Lambda. */
  outputGridHz: number | null;
  /** Número total de linhas. */
  rows: number;
  /** Unidade por canal (null = desconhecida), do `channel_metadata` do schema. */
  units: Record<string, string | null>;
  /** Metadados completos por canal (`channel_metadata` do schema). */
  channelMetadata: Record<string, ChannelMetadata>;
  /** Linhas do log antes do downsampling, quando informado. */
  rowsOriginal: number | null;
  /** Se o servidor reduziu os pontos (LTTB), quando informado. */
  downsampled: boolean | null;
  /** Espaço de valores dos dados (`stored_parquet`), quando informado. */
  valueSpace: string | null;
}

export interface FileChannelsError {
  errorCode: string;
  message: string;
  status: number;
  /** Contexto devolvido pelo servidor (`{error_code, message, context}`). */
  context?: unknown;
}

export type FtFileType = 'ftlog' | 'ftml';

export interface FileChannelsRequestOptions {
  /** "ftlog" para logs, "ftml" para mapas. Obrigatorio... */
  fileType: FtFileType;
  /** Se true, ativa downsampling LTTB no servidor. */
  lttb?: boolean;
  /** Limite de pontos por canal quando lttb=true. */
  maxPoints?: number;
}

function fileChannelsError(errorCode: string, message: string, status = 0): FileChannelsError {
  return { errorCode, message, status };
}

function isFileChannelsError(err: unknown): err is FileChannelsError {
  return (
    typeof err === 'object' &&
    err !== null &&
    typeof (err as FileChannelsError).errorCode === 'string' &&
    typeof (err as FileChannelsError).status === 'number'
  );
}

/** `missing_channels` do Arrow vem em CSV (`A,B`) — formato confirmado pelo backend em DEV. */
function parseMissingChannels(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** `channel_metadata` JSON do schema; formato inválido é tratado como ausente. */
function parseChannelMetadata(raw: string | undefined): Record<string, ChannelMetadata> | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, ChannelMetadata>;
    }
  } catch {
    /* sem metadados JSON */
  }
  return null;
}

/** Legado `channel_units` ("canal:unidade,canal:unidade"); só quando não há JSON. */
function parseLegacyUnits(raw: string | undefined): Record<string, string | null> {
  const units: Record<string, string | null> = {};
  for (const pair of (raw ?? '').split(',')) {
    const sep = pair.indexOf(':');
    if (sep <= 0) continue;
    const unit = pair.slice(sep + 1).trim();
    units[pair.slice(0, sep).trim()] = unit.length > 0 ? unit : null;
  }
  return units;
}

@Injectable({ providedIn: 'root' })
export class FileChannelsService {
  constructor(private http: HttpClient) {}

  read(
    userID: string,
    fileID: string,
    channels: string[],
    opts: FileChannelsRequestOptions,
  ): Observable<FileChannelsResult> {
    const body: Record<string, unknown> = {
      channels,
      file_type: opts.fileType,
    };
    if (opts.lttb !== undefined) body['lttb'] = opts.lttb;
    if (opts.maxPoints !== undefined) body['max_points'] = opts.maxPoints;

    return this.http
      .post(this.url(userID, fileID), body, {
        responseType: 'arraybuffer',
        observe: 'response',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(
        map((resp) => this.decodeArrow(resp)),
        catchError((err: unknown) => throwError(() => this.toError(err))),
      );
  }

  /** Catálogo de canais do arquivo (`channels_list`, contrato §11). Não aceita display_projections. */
  listChannels(
    userID: string,
    fileID: string,
    fileType: FtFileType,
  ): Observable<ChannelsListResponse> {
    return this.http
      .post<ChannelsListResponse>(this.url(userID, fileID), {
        file_type: fileType,
        resource: 'channels_list',
      })
      .pipe(catchError((err: unknown) => throwError(() => this.toError(err))));
  }

  /** Estatísticas por canal (`stats`, contrato §12). Valida os limites antes de enviar. */
  stats(
    userID: string,
    fileID: string,
    fileType: FtFileType,
    req: LogStatsRequest,
  ): Observable<LogStatsResponse> {
    const invalid = this.validateStatsRequest(req);
    if (invalid) {
      return throwError(() => fileChannelsError('INVALID_STATS_REQUEST', invalid));
    }
    return this.http
      .post<LogStatsResponse>(this.url(userID, fileID), {
        file_type: fileType,
        resource: 'stats',
        ...req,
      })
      .pipe(catchError((err: unknown) => throwError(() => this.toError(err))));
  }

  private url(userID: string, fileID: string): string {
    return `${enviroment.BASE_URL_API}/user/${userID}/file/${fileID}/channels`;
  }

  /** Mensagem do primeiro limite violado, ou null se o request é válido. */
  private validateStatsRequest(req: LogStatsRequest): string | null {
    const { channels, start_ms, end_ms, max_points, bucket_ms, display_projections } = req;
    if (channels.length < 1 || channels.length > MAX_STATS_CHANNELS) {
      return `Selecione de 1 a ${MAX_STATS_CHANNELS} canais.`;
    }
    if (
      channels.some(
        (c, i) =>
          c.trim().length === 0 || NON_STATS_CHANNELS.includes(c) || channels.indexOf(c) !== i,
      )
    ) {
      return 'Canais vazios, repetidos ou de tempo não são aceitos.';
    }
    if (start_ms !== undefined && !(Number.isFinite(start_ms) && start_ms >= 0)) {
      return 'start_ms deve ser >= 0.';
    }
    if (end_ms !== undefined && !(Number.isFinite(end_ms) && end_ms >= 0)) {
      return 'end_ms deve ser >= 0.';
    }
    if (start_ms !== undefined && end_ms !== undefined && end_ms < start_ms) {
      return 'end_ms deve ser >= start_ms.';
    }
    if (
      max_points !== undefined &&
      !(
        Number.isInteger(max_points) &&
        max_points >= MIN_STATS_POINTS &&
        max_points <= MAX_STATS_POINTS
      )
    ) {
      return `max_points deve estar entre ${MIN_STATS_POINTS} e ${MAX_STATS_POINTS}.`;
    }
    if (
      bucket_ms !== undefined &&
      !(Number.isInteger(bucket_ms) && bucket_ms >= MIN_BUCKET_MS && bucket_ms <= MAX_BUCKET_MS)
    ) {
      return `bucket_ms deve estar entre ${MIN_BUCKET_MS} e ${MAX_BUCKET_MS}.`;
    }
    if (display_projections) {
      const issues = validateDisplayProjections(channels, display_projections);
      if (issues.length > 0) return issues.join(' ');
    }
    return null;
  }

  /** Confere o Content-Type antes de decodificar: falhas podem voltar JSON com status 200. */
  private decodeArrow(resp: HttpResponse<ArrayBuffer>): FileChannelsResult {
    const contentType = (resp.headers.get('content-type') ?? '').toLowerCase();
    if (!contentType.startsWith(ARROW_CONTENT_TYPE)) {
      throw this.fromBody(resp.body, resp.status);
    }
    let table: Table;
    try {
      table = tableFromIPC(new Uint8Array(resp.body!));
    } catch (e) {
      throw fileChannelsError(
        'INVALID_ARROW_STREAM',
        e instanceof Error ? e.message : 'Stream Arrow inválido.',
        resp.status,
      );
    }
    return this.toResult(table, resp.headers.get('x-missing-channels'));
  }

  /** Lê um corpo que não é Arrow como texto/JSON `{error_code, message, context}`. */
  private fromBody(body: ArrayBuffer | null, status: number): FileChannelsError {
    const text = body ? new TextDecoder().decode(body) : '';
    try {
      const parsed = JSON.parse(text);
      return {
        errorCode: parsed.error_code ?? 'UNEXPECTED_CONTENT_TYPE',
        message: parsed.message ?? text,
        status,
        context: parsed.context,
      };
    } catch {
      return fileChannelsError('UNEXPECTED_CONTENT_TYPE', text || 'Resposta não é Arrow.', status);
    }
  }

  /**
   * Valores nulos ficam ausentes (NaN): `toArray()` devolve o buffer cru e ignora a validade,
   * então com nulos monta-se uma cópia. Sem nulos mantém o caminho rápido (zero cópia).
   */
  private columnToArray(col: NonNullable<ReturnType<Table['getChild']>>): ChannelSeries {
    const raw = col.toArray() as ChannelSeries;
    if (col.nullCount === 0) return raw;
    const out = raw instanceof Float32Array ? Float32Array.from(raw) : Float64Array.from(raw);
    for (let i = 0; i < out.length; i++) {
      if (!col.isValid(i)) out[i] = NaN;
    }
    return out;
  }

  private toResult(table: Table, missingHeader: string | null): FileChannelsResult {
    const series: Record<string, ChannelSeries> = {};
    let sampleTime: Float32Array | null = null;

    for (const field of table.schema.fields) {
      const col = table.getChild(field.name);
      if (!col) continue;
      const data = this.columnToArray(col);
      if (field.name === 'Sample_Time') {
        sampleTime = data instanceof Float32Array ? data : Float32Array.from(data);
      } else {
        series[field.name] = data;
      }
    }

    if (!sampleTime) {
      throw fileChannelsError(
        'INVALID_ARROW_SCHEMA',
        'Arrow stream sem coluna Sample_Time — schema inesperado.',
      );
    }

    const meta = table.schema.metadata;
    // Metadados do schema primeiro; o header só vale quando o schema não traz a chave.
    const missing = parseMissingChannels(meta.get('missing_channels') ?? missingHeader ?? '');

    const channelMetadata = parseChannelMetadata(meta.get('channel_metadata'));
    const units: Record<string, string | null> = channelMetadata
      ? Object.fromEntries(Object.entries(channelMetadata).map(([ch, m]) => [ch, m.unit ?? null]))
      : parseLegacyUnits(meta.get('channel_units'));

    const outputGridHz = parseFloat(meta.get('output_grid_hz') ?? '');
    const rowsOriginal = parseInt(meta.get('rows_original') ?? '', 10);
    const downsampled = meta.get('downsampled');

    return {
      sampleTime,
      series,
      missing,
      outputGridHz: Number.isFinite(outputGridHz) ? outputGridHz : null,
      rows: table.numRows,
      units,
      channelMetadata: channelMetadata ?? {},
      rowsOriginal: Number.isFinite(rowsOriginal) ? rowsOriginal : null,
      downsampled: downsampled === undefined ? null : downsampled.toLowerCase() === 'true',
      valueSpace: meta.get('value_space') ?? null,
    };
  }

  private toError(err: unknown): FileChannelsError {
    if (isFileChannelsError(err)) return err;
    if (!(err instanceof HttpErrorResponse)) {
      return fileChannelsError(
        'UNKNOWN_ERROR',
        err instanceof Error ? err.message : 'Erro inesperado.',
      );
    }

    let errorCode = 'NETWORK_ERROR';
    let message = err.message ?? 'Erro de rede.';
    let context: unknown;

    if (err.error instanceof ArrayBuffer) {
      try {
        const text = new TextDecoder().decode(err.error);
        const parsed = JSON.parse(text);
        errorCode = parsed.error_code ?? errorCode;
        message = parsed.message ?? message;
        context = parsed.context;
      } catch {
        /* fica com fallback */
      }
    } else if (typeof err.error === 'object' && err.error?.error_code) {
      errorCode = err.error.error_code;
      message = err.error.message ?? message;
      context = err.error.context;
    }

    return { errorCode, message, status: err.status, context };
  }
}
